import React, {
    memo,
    useCallback,
    useEffect,
    useState,
} from 'react';

import ReactDOM from 'react-dom';
import {
    useDispatch,
    useSelector,
} from 'react-redux';

import dynamic from 'next/dynamic';

import _get from 'lodash/get';

// bonnet
import { useDevice } from '@bonnet/next/device';
import { ErrorBoundary } from '@bonnet/next/error-boundary';

import { useFeatures } from '@atc/features';

import { sendClick } from 'reaxl-analytics';

// analytics
import timingUtil from '@/utilities/timingUtil';

import useDataLayer from '@/analytics/useDataLayer';

// modules
import authDuck from '@/ducks/authDuck.js';
import dealsDuck from '@/ducks/dealsDuck.js';
import paymentsDuck from '@/ducks/paymentsDuck.js';
import recentSearchDuck from '@/ducks/recentSearchDuck.js';
import savedInventoryDuck from '@/ducks/savedInventoryDuck.js';
import userDuck from '@/ducks/userDuck.js';
import userPreferencesDuck from '@/ducks/userPreferencesDuck.js';

import useVdpNavigation from '@/hooks/useVdpNavigation';

import PersonalizationEngineModule from '@/modules/PersonalizationEngineModule';

// Utilities

import LazyComponent from '@/components/LazyComponent';

const AuthenticationHeader = dynamic(() => import('reaxl-organisms/es/AuthenticationHeader'), { ssr: false });

function HeaderContentContainer({
    listing = {},
    onLoggedInStateReady = () => { },
    saveListing,
    sendAlertClickHandler,
    showHeaderContent = true,
    showEmailSubscribe = false,
    onLogInSuccess = () => { },
    onSignOutClick = () => { },
    onEmailSubscribe = () => { },
    ...rest
}) {
    const [headerContentRef, changeHeaderContentRef] = useState(null);
    const dataLayer = useDataLayer();
    const dispatch = useDispatch();
    const device = useDevice();

    const isLg = _get(device, 'is.lg', false);

    const isLoggedIn = useSelector(authDuck.selectors.isLoggedIn);
    const peData = useSelector(PersonalizationEngineModule.duck.selectors.selectPersonalization);
    const {
        authentication_header: [enableAuthenticationHeader],
        my_atc_shareable_link: [enableShareListings],
        my_wallet: [enableMyWallet, { show_popover: showWalletPopover }],
    } = useFeatures(['authentication_header', 'my_atc_shareable_link', 'my_wallet']);

    const isClientRendered = typeof window !== 'undefined';
    const popoverContainerEl = isClientRendered && document.body;

    const navigateToVdp = useVdpNavigation();

    useEffect(() => {
        if (isClientRendered && headerContentRef === null && enableAuthenticationHeader) {
            changeHeaderContentRef(document.getElementsByClassName('header-content'));
            timingUtil.markUserTime('header-rendered');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isClientRendered]);

    const handleQuickSaveItemClick = async (event, quickSaveListing) => {
        dispatch(userDuck.creators.hideQuickSaveList());
        await navigateToVdp({ listingId: quickSaveListing.id });
    };

    const handleQuickSaveItemToggle = useCallback((event, savedListing, wasSaved) => {
        saveListing({
            event,
            listing: savedListing,
            wasSaved,
            sendClick,
        });
    }, [saveListing]);

    const handleSaveInventoryUpdate = useCallback((data) => {
        sendAlertClickHandler({
            data,
            sendClick,
            peData,
            dataLayer,
            saveType: 'car',
        });
    }, [sendAlertClickHandler, peData, dataLayer]);

    const handleSaveSearchUpdate = useCallback((data) => {
        sendAlertClickHandler({
            data,
            sendClick,
            peData,
            dataLayer,
            saveType: 'search',
        });
    }, [sendAlertClickHandler, peData, dataLayer]);

    const handleClick = (e, data) => {
        sendClick('headerGeneralEventHandler', e, { ...data });
    };

    const handleShareListingsClick = useCallback(async (/* listings */) => {
        await dispatch(userPreferencesDuck.creators.setKeys({ showShareListingsModal: true }));
    }, [dispatch]);

    const renderPersonalizationHeader = useCallback(() => (
        (
            <LazyComponent fallback={null}>
                <ErrorBoundary>
                    <AuthenticationHeader
                        onClick={handleClick}
                        enableMyWallet={enableMyWallet}
                        enableWalletPopover={isLg && showWalletPopover}
                        onLoggedInStateReady={onLoggedInStateReady}
                        onLogInSuccess={onLogInSuccess}
                        onSignOutClick={onSignOutClick}
                        onShareListingsClick={enableShareListings && handleShareListingsClick}
                        onQuickSaveItemClick={handleQuickSaveItemClick}
                        onQuickSaveItemToggle={handleQuickSaveItemToggle}
                        onEmailSubscribe={onEmailSubscribe}
                        onSaveInventoryUpdate={handleSaveInventoryUpdate}
                        onSaveSearchUpdate={handleSaveSearchUpdate}
                        paymentsDuck={paymentsDuck}
                        dealsDuck={dealsDuck}
                        showEmailSubscribe={showEmailSubscribe}
                        userDuck={userDuck}
                        recentSearchDuck={recentSearchDuck}
                        savedInventoryDuck={savedInventoryDuck}
                        isLoggedIn={isLoggedIn}
                        showPsxNotificationsCount
                        portalTarget={popoverContainerEl}
                        {...rest}
                    />
                </ErrorBoundary>
            </LazyComponent>
        )
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ), [
        onLoggedInStateReady,
        onLogInSuccess,
        onSignOutClick,
        handleQuickSaveItemToggle,
        handleSaveInventoryUpdate,
        handleSaveSearchUpdate,
        onEmailSubscribe,
    ]);

    if (typeof window !== 'undefined' && headerContentRef && showHeaderContent && enableAuthenticationHeader) {
        return ReactDOM.createPortal(
            renderPersonalizationHeader(),
            headerContentRef[0]
        );
    }

    return null;

}

export default memo(HeaderContentContainer);
